import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { EllipsisVertical } from 'lucide-react'
import { emit } from '../../platform/analytics/analytics'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { isLastOwner, leaveBlockReason, narrowRole } from './roles'
import { useRowMutation } from './useRowMutation'

/**
 * The row's "More actions" menu — Task 6, alongside `RoleMenu`'s role
 * control. `MemberRow` renders this only when the viewer `canManageMembers`
 * or the row is their OWN — see `MemberRow.tsx`'s call site for the
 * remaining guards. Two items, never both: **Remove** on someone else's row,
 * **Leave** on your own — the server enforces the split with two different
 * rejections ("Use leave endpoint to remove yourself" going one way, and
 * `leave_household` being the only endpoint that acts on your own membership
 * going the other), so the client offers only the one the server would
 * accept.
 *
 * **A second menu button beside `RoleMenu`'s, not a shared one.** Raised with
 * the team lead rather than decided here: `MemberRow`'s trailing slot now
 * holds two adjacent menu triggers for a row where both `canChangeRole` and
 * `canManageMembers` are true (an owner acting on a non-owner's row). Built
 * this way because the plan's file list and interface name the two
 * separately and `RoleMenu`'s own scope is a single value (the role), not a
 * set of unrelated actions — merging Remove into it would make one control
 * answer two different questions. Whether that is the right call for the
 * design system is exactly what needs a second, fresh-context opinion.
 *
 * **Built by hand, mirroring `RoleMenu` exactly** rather than sharing a
 * generic popup-menu component with it — the shared aria-menu plumbing (open/
 * close, outside-click, Escape, arrow-key roving focus, `aria-disabled` over
 * native `disabled`) is real duplication, and extracting it is deferred for
 * the same reason `RoleMenu`'s own docblock deferred `useRowMutation()`:
 * three call sites earn an extraction, and this repo now has exactly two
 * hand-rolled menus.
 *
 * **`--surface` (opaque), matching `.role-menu__panel` exactly, and for the
 * identical stated reason** — `household.css`'s own note on that panel names
 * THIS menu as "where the `--glass-2` measurement belongs, once one of the
 * two menus can carry it" (web-architect review, issue 312, blocking finding
 * 3). Not built here: it needs a `controlTokens.test.ts` `CONTROLS` row and a
 * contrast measurement, which is issue 363, out of this task's scope by the
 * team lead's own instruction.
 *
 * **The Leave item's own last-owner guard — Task 7, tracke5-webapp#314.**
 * `blocked` (`isLastOwner` on the Remove path) was computed here from Task 6
 * onward but never consulted for Leave, because `onLeave` had no caller yet
 * (`t6-architect.md`'s own finding, under "Two adjacent menu triggers").
 * `leaveReason` below closes that: the server refuses "Cannot leave as last
 * owner" identically to "Cannot remove the last owner", so the item needs the
 * same pre-empting guard Remove already had.
 */

type MemberActionsMenuProps = {
  /** The membership id this menu acts on — `DELETE /members/{membership_id}`
   *  takes this, never the user id. */
  membershipId: string
  /** `rawRole`, for `isLastOwner` — the wire value, not the narrowed one,
   *  matching `RoleMenu`'s own `rawRole` prop for the identical reason. */
  rawRole: string
  /** The member's display name or email, for the trigger's accessible name
   *  and the confirmation copy. Resolved once by `MemberRow`. */
  label: string
  /** The full roster, `rawRole` only — what `isLastOwner` needs to decide
   *  whether this row is the household's only owner. */
  members: readonly { rawRole: string }[]
  /** Whether this row is the signed-in viewer's own. Decides which of the
   *  two items renders — never both. */
  isSelf: boolean
  /** `undefined` renders no Remove item at all, matching `RoleMenu`'s own
   *  rule: a control with nowhere to send its result is worse than a dead
   *  one. `Household.tsx` passes a wrapper around `useRemoveMember`'s own
   *  `remove` that also raises a toast once it settles (issue 366) — this
   *  component only ever sees "a promise", and does not need to know that. */
  onRemove?: (membershipId: string) => Promise<void>
  /**
   * `undefined` renders no Leave item at all, for the same reason.
   * `Household.tsx` passes `() => setLeaving(true)` — the SAME handler its
   * own footer control uses, opening the SAME confirmation, so a viewer who
   * looks for "leave" in their own row's menu and one who looks for it in
   * the footer land on one flow, not two (Task 7, tracke5-webapp#314).
   * `MemberActionsMenu` itself never owns a dialog for this path — see
   * `restoreFocusTo`'s own docblock below for what that means for focus.
   */
  onLeave?: () => void
  /**
   * Where focus goes after a CONFIRMED remove, and it must be OUTSIDE this
   * row — web-architect review, issue 313, blocking finding B1.
   *
   * A successful remove invalidates the members query and the row leaves the
   * list, so `triggerRef` (this component's own fallback, below) is a
   * detached node by the time `Overlay` restores focus: `focusFirstAlive`
   * skips it and refuses `<body>`, so restore silently fails. Worse than
   * obvious, because `setConfirming(false)` runs before the refetch lands —
   * restore APPEARS to succeed and focus is then dropped a moment later when
   * the row unmounts. `InvitationRow.tsx`'s own `restoreFocusTo` docblock
   * carries the identical defect for revoke, found first there; jsdom cannot
   * see either, which is why this needed a real browser (see the report).
   *
   * `Household.tsx` passes `inviteButton` — the same anchor already used for
   * `InvitationRow`'s revoke — because it survives every path Remove can
   * close on. Left `undefined` by every unit test in this directory except
   * `Household.remove.test.tsx`, which is the only one that renders the real
   * wiring; `MemberRow.remove.test.tsx` exercises the LOCAL fallback instead.
   *
   * **The fallback still has no Leave path to serve.** `ConfirmDialog` below
   * renders only under `canRemove` — Leave calls `onLeave` straight from the
   * menu item, with no dialog owned by THIS component at all. Task 7
   * (tracke5-webapp#314) gives Leave its own confirmation, but it lives in
   * `Household.tsx`, one level up, alongside the footer control that opens
   * the same one: a row this Leave destroys is the viewer's OWN, which is
   * exactly the row a Remove-shaped `restoreFocusTo` (anchored inside this
   * component) cannot serve either. `Household.tsx` anchors that dialog on
   * its own footer button instead — a control that is never part of the
   * member list and so survives the household changing under the viewer,
   * unlike anything in this row. This prop's `??` fallback therefore still
   * only matters to the Remove path, and only to a unit test that renders
   * `MemberRow` alone without supplying it.
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
}

export function MemberActionsMenu({
  membershipId,
  rawRole,
  label,
  members,
  isSelf,
  onRemove,
  onLeave,
  restoreFocusTo,
}: MemberActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const reasonId = useId()

  // True only when `rawRole` is the household's sole owner — the guard the
  // server enforces with "Cannot remove the last owner", pre-empted here so
  // the click never reaches that 400.
  const blocked = isLastOwner(
    members.map((m) => ({ role: m.rawRole })),
    { role: rawRole },
  )

  const canRemove = !isSelf && onRemove !== undefined
  const canLeave = isSelf && onLeave !== undefined

  /**
   * The Leave path's own reason — Task 7, tracke5-webapp#314. `leaveBlockReason`
   * takes the SAME `rawRole`/`members` this component already has (they are
   * the viewer's own values whenever `isSelf` is true), so this can never
   * disagree with `Household.tsx`'s footer control, which calls the identical
   * function on the identical data. Computed unconditionally rather than only
   * under `canLeave` — cheap, and it keeps the render below from having to
   * guard against reading it before it exists.
   */
  const leaveReason = leaveBlockReason(rawRole, members)

  // `run`'s own `failure` is unused here on purpose: `ConfirmDialog` already
  // awaits `onConfirm`, stays open on rejection and renders its own reason —
  // duplicating that on the row would show the same failure twice. `pending`
  // still guards the trigger against a re-open while a confirmed removal is
  // in flight, the same defence-in-depth `RoleMenu`'s trigger gives its own
  // action (see `useRowMutation`'s own docblock).
  const removeMutation = useRowMutation(
    (id: string) => (onRemove ? onRemove(id) : Promise.reject(new Error('no onRemove supplied'))),
  )

  useEffect(() => {
    if (!open) return
    function onDocumentPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onDocumentKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDocumentPointerDown)
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown)
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [open])

  useEffect(() => {
    // Opens onto its first item — same ARIA menu-button pattern `RoleMenu`
    // follows, and the same reason: a keyboard or screen-reader user needs
    // the menu itself to move focus in. No item here is ever native
    // `disabled` (see the item render below), so this always lands.
    if (open) itemRefs.current[0]?.focus()
  }, [open])

  if (!canRemove && !canLeave) return null

  return (
    <div className="member-actions" ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="chip"
        size="panel"
        className="member-actions__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${label}`}
        aria-busy={removeMutation.pending}
        onClick={() => {
          if (removeMutation.pending) return
          setOpen((o) => !o)
        }}
      >
        <Icon glyph={EllipsisVertical} label="More actions" />
      </Button>
      {open && (
        <div className="member-actions__panel" role="menu" aria-label={`Actions for ${label}`}>
          {canRemove && (
            <Button
              ref={(el) => {
                itemRefs.current[0] = el
              }}
              role="menuitem"
              // `aria-disabled`, not `disabled` — the same B1 reasoning
              // `RoleMenu`'s item render documents: a native `disabled` item
              // cannot receive focus, which is what left THAT menu
              // unreachable by keyboard on an admin/owner row.
              aria-disabled={blocked || undefined}
              aria-describedby={blocked ? reasonId : undefined}
              variant="chip"
              size="panel"
              className="member-actions__item"
              onClick={() => {
                // `aria-disabled` still dispatches a click — this is the
                // guard native `disabled` would otherwise give for free.
                if (blocked) return
                setOpen(false)
                setConfirming(true)
              }}
            >
              Remove
            </Button>
          )}
          {canLeave && (
            <Button
              ref={(el) => {
                itemRefs.current[0] = el
              }}
              role="menuitem"
              // Same `aria-disabled` reasoning as Remove, above — and the
              // same guard against the fail-closed branch answering `true`
              // for a role that could not even be classified: `leaveReason`
              // is null only when leaving is genuinely allowed.
              aria-disabled={leaveReason !== null || undefined}
              aria-describedby={leaveReason !== null ? reasonId : undefined}
              variant="chip"
              size="panel"
              className="member-actions__item"
              onClick={() => {
                // `aria-disabled` still dispatches a click — the guard
                // native `disabled` would otherwise give for free.
                if (leaveReason !== null) return
                setOpen(false)
                onLeave?.()
              }}
            >
              Leave
            </Button>
          )}
          {canRemove && blocked && (
            <p id={reasonId} className="member-actions__reason">
              {label} is the household&rsquo;s last owner — make someone else an owner first.
            </p>
          )}
          {canLeave && leaveReason !== null && (
            <p id={reasonId} className="member-actions__reason">
              {leaveReason}
            </p>
          )}
        </div>
      )}
      {canRemove && (
        <ConfirmDialog
          open={confirming}
          mode="remove_member"
          title={`Remove ${label}?`}
          /**
           * Two claims, both true today — a third was cut, not replaced.
           * `remove_member` does not run `leave_household`'s account-sharing
           * cleanup (`useMembers.ts`'s own `useRemoveMember` docblock has the
           * full trace), so a removed member keeps whatever account-sharing
           * grants they already had — "will lose access to everything shared
           * here" was false, and a destructive confirmation stating a
           * security property the system does not provide is worse than
           * saying less (web-architect review, issue 313, blocking finding
           * B2). Filed as `expense-monitor-backend#314`; restore the third
           * clause here once that lands, rather than warning about a bug
           * that is now filed.
           */
          body={
            `${label}'s accounts and transactions stay with ${label} — nothing is deleted. ` +
            'They will no longer appear in this household.'
          }
          confirmLabel="Remove"
          destructive
          // The caller's anchor when it gave one (`inviteButton`, which
          // survives the removal); `triggerRef` — INSIDE the row this
          // confirmation is about to destroy — is only the fallback for a
          // caller that doesn't supply one, matching `InvitationRow.tsx`'s
          // `restoreFocusTo ?? revokeButton` for the identical shape.
          restoreFocusTo={restoreFocusTo ?? triggerRef}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            await removeMutation.run(membershipId)
            // Task 12. `role`, never `label` — the row's display name is
            // exactly what `vocabulary.ts` refuses. `narrowRole` can return
            // `null` for a value outside the enum (issue 306); the property
            // is simply omitted then, matching every other "not measured"
            // key in this module rather than sending a value the allow-list
            // would reject.
            const role = narrowRole(rawRole)
            emit('member_removed', role ? { role } : {})
            setConfirming(false)
          }}
        />
      )}
    </div>
  )
}
